/**
 * Turning knowledge documents that were pasted out of Word into readable
 * Markdown, for DISPLAY and editing.
 *
 * ⚠️ Nothing here writes to the database. The stored `full_text` only changes
 *    when someone edits and saves, and then it is saved as this structured
 *    Markdown (still plain text the analyst can read). Every word of the input
 *    is kept: these functions only add structure (#, -, |) around lines.
 *
 * What a Word paste looks like, and what each pattern becomes:
 *   - one paragraph per line, single newlines           → separate paragraphs
 *   - ALL CAPS section titles                           → ## heading
 *   - a short title line that starts a section         → ### heading
 *   - "\t•\t" / "•" / "◦" bullets                        → - list items
 *   - "☐\t" checklist lines                             → - [ ] to-do items
 *   - a table pasted as one row per line, cells split by tabs:
 *       Characteristic<TAB>Detail / Industry fit<TAB>…   → | table |
 *   - one "label<TAB>value" line on its own              → **label** value
 *   - "01<TAB>Business Overview and Goals"               → ## heading
 *   - a table flattened to one cell per line (2–8 columns):
 *       Characteristic / Detail / Industry fit / <text> … → | table |
 *   - "SECTION 2 — …" lines                              → ## heading
 */

const STRUCTURAL = /^\s*([-*+]\s|\d+[.)]\s|>|\||#{1,6}\s|```|~~~)/;
const BULLET = /^\s*[•◦▪●‣∙·]\s*/;
/** A Word checklist line: ☐ is open, ☑ and ☒ are ticked. */
const CHECKBOX = /^\s*([☐☑☒])\s*/;

/** A row of a table Word pasted with tabs between the cells (not a bullet or a checklist line). */
function isTabRow(line: string | undefined): boolean {
  if (line === undefined) return false;
  const t = line.trim();
  return t.includes("\t") && !BULLET.test(t) && !CHECKBOX.test(t);
}

/** The cells of a tab row. Only spaces are trimmed off the ends, so an empty first cell keeps its column. */
const cellsOf = (line: string) => line.replace(/^ +| +$/g, "").split("\t").map((c) => c.trim());

/** Already Markdown (headings, lists or tables) → leave its structure alone. */
function looksLikeMarkdown(lines: string[]): boolean {
  let marks = 0;
  for (const l of lines) if (/^\s*(#{1,6}\s|[-*+]\s\S|\|.*\|)/.test(l)) marks++;
  return marks >= 2;
}

const words = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

/** A heading-shaped line: short, capitalised, no sentence punctuation at the end. */
function isTitleLike(line: string): boolean {
  const t = line.trim();
  // A tab inside the line separates the cells of a table row ("Characteristic<TAB>Detail"): never a title.
  if (t.includes("\t")) return false;
  if (t.length < 2 || t.length > 80 || words(t) > 12) return false;
  if (BULLET.test(t) || STRUCTURAL.test(t)) return false;
  if (/[.,;:!?]$/.test(t)) return false;
  if (t.includes("·")) return false; // "Category: SOPs · June 2026" is metadata, not a title
  if (/\s=\s/.test(t)) return false; // "Example: … CPC = $500 / 200 = $2.50" is a worked formula
  return /^[A-Z0-9"“'(]/.test(t);
}

function isAllCaps(line: string): boolean {
  const t = line.trim();
  const letters = t.replace(/[^A-Za-z]/g, "");
  return letters.length >= 4 && t.length <= 120 && letters === letters.toUpperCase() && !/[.!?]$/.test(t) && !t.includes("·");
}

/** "Ask this", "The handle", "When ready, ask": a short sentence-case cue, shown bold. */
const isCue = (line: string) => words(line) <= 4 && line.trim().length <= 32 && /\s[a-z]/.test(line.trim());
const isLong = (line: string) => line.trim().length > 60 && !isTitleLike(line);
const isHeaderCell = (line: string) => isTitleLike(line) && words(line) <= 4 && line.trim().length <= 32 && !isAllCaps(line);
/** A client name like "CEMCO" is a table cell, not a section title. */
const capsOk = (line: string) => !isAllCaps(line) || words(line) <= 2;
const cell = (s: string) => s.trim().replace(/\|/g, "\\|");

/** "SECTION 2 — GEO vs SEO" and friends are section titles even when not all caps. */
const isSectionTitle = (line: string) => /^(SECTION|PART|APPENDIX|MODULE)\s+[0-9IVX]+\b/.test(line.trim()) && line.trim().length <= 120;

/**
 * A table Word flattened to one cell per line, starting at `i`.
 *
 * The header is a run of short cells; the FIRST row's label is short too, so
 * the run is "header cells + one label" and ends at the first longer cell. That
 * fixes the column count n. Rows are then read n lines at a time while each
 * row starts with a short label. Needs at least two rows.
 */
function readTableN(lines: string[], i: number, n: number): { rows: string[][]; used: number } | null {
  if (n < 2 || n > 8 || i + n > lines.length) return null;
  const head = lines.slice(i, i + n);
  if (head.some((c) => !c.trim() || isSectionTitle(c) || !isTitleLike(c) || c.trim().length > 60)) return null;
  const rows: string[][] = [head.map((c) => c.trim())];
  let j = i + n;
  while (j + n <= lines.length) {
    const row = lines.slice(j, j + n);
    // A blank line, a section title or a bullet ends the table.
    if (row.some((c) => !c.trim() || isSectionTitle(c) || BULLET.test(c.trim()) || !capsOk(c))) break;
    if (!isTitleLike(row[0]) || row[0].trim().length > 60) break;
    rows.push(row.map((c) => c.trim()));
    j += n;
  }
  return rows.length >= 3 ? { rows, used: j - i } : null;
}

function readTable(lines: string[], i: number): { rows: string[][]; used: number } | null {
  let r = 0;
  while (i + r < lines.length && lines[i + r].trim() && isHeaderCell(lines[i + r]) && !isSectionTitle(lines[i + r])) r++;
  if (r < 2) return null;
  // The run is either just the header, or the header plus a short first label.
  const options = [readTableN(lines, i, r - 1), readTableN(lines, i, r)].filter(Boolean) as Array<{ rows: string[][]; used: number }>;
  if (!options.length) return null;
  return options.sort((x, y) => y.used - x.used || tableScore(y.rows) - tableScore(x.rows))[0];
}

const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / Math.max(xs.length, 1);
/** Tables here are "short label → longer values"; used to pick the right alignment. */
const tableScore = (rows: string[][]) =>
  avg(rows.slice(1).map((r) => avg(r.slice(1).map((c) => c.length)))) - avg(rows.slice(1).map((r) => r[0].length));

/** Word paste → structured Markdown. Markdown input only gets paragraph splitting. */
export function plainTextToMarkdown(text: string): string {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  if (!looksLikeMarkdown(lines)) return structurePlainText(lines);
  const out: string[] = [];
  let fence = false;
  lines.forEach((line, i) => {
    // One blank line between blocks: the editor reads more as empty paragraphs.
    if (!fence && !line.trim() && out.length && !out[out.length - 1].trim()) return;
    out.push(line);
    if (/^\s*(```|~~~)/.test(line)) fence = !fence;
    const next = lines[i + 1];
    if (!fence && next !== undefined && line.trim() && next.trim() && !STRUCTURAL.test(line) && !STRUCTURAL.test(next) && !/^\s{2,}/.test(next))
      out.push("");
  });
  return out.join("\n");
}

/** A Markdown table: the first row is the header. */
function markdownTable(rows: string[][]): string {
  const [head, ...body] = rows;
  const row = (cells: string[]) => `| ${cells.map(cell).join(" | ")} |`;
  return [row(head), `| ${head.map(() => "---").join(" | ")} |`, ...body.map(row)].join("\n");
}

/** Bold text that cannot be cut short by an asterisk inside it. */
const bold = (s: string) => `**${s.trim().replace(/\*/g, "\\*")}**`;

/** "<head>" in running text is text, not HTML for the editor to drop on save: its "<" is escaped. */
const escapeTags = (s: string) => s.replace(/<(?=[A-Za-z/!?])/g, "\\<");

/** An HTML block pasted as an example (a JSON-LD <script>) runs to its closing tag. */
function readHtmlBlock(lines: string[], i: number): number | null {
  const open = /^\s*<(script|style|noscript)\b[^>]*>/i.exec(lines[i]);
  if (!open) return null;
  const close = new RegExp(`</${open[1]}\\s*>`, "i");
  for (let j = i; j < lines.length; j++) if (close.test(lines[j])) return j;
  return null;
}

/**
 * Rows Word pasted with tabs between the cells, from `i` to the first blank,
 * bullet or tab-free line. Short rows are padded to the widest one, and a first
 * column that is empty in every row (an indent, not data) is dropped.
 */
function readTabRows(lines: string[], i: number): { rows: string[][]; used: number } {
  const rows: string[][] = [];
  let j = i;
  while (j < lines.length && isTabRow(lines[j])) rows.push(cellsOf(lines[j++]));
  if (rows.every((r) => r.length > 1 && !r[0])) rows.forEach((r) => r.shift());
  const width = Math.max(...rows.map((r) => r.length));
  return { rows: rows.map((r) => [...r, ...Array<string>(width - r.length).fill("")]), used: j - i };
}

function structurePlainText(raw: string[]): string {
  // Blank lines are kept: they are what ends a flattened table.
  const lines = raw.map((l) => l.replace(/\s+$/, ""));
  const nextFilled = (from: number) => { let k = from; while (k < lines.length && !lines[k].trim()) k++; return k; };
  const blocks: string[] = [];
  let list: string[] = [];
  let listKind: "bullet" | "task" = "bullet";
  const flushList = () => { if (list.length) { blocks.push(list.join("\n")); list = []; } };
  // Bullets and to-do items are separate lists, so a switch between them starts a new one.
  const listItem = (kind: "bullet" | "task", item: string) => {
    if (kind !== listKind) flushList();
    listKind = kind;
    list.push(item);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const t = line.trim();
    if (!t) { continue; }

    if (BULLET.test(t)) { listItem("bullet", `- ${t.replace(BULLET, "")}`); continue; }
    const box = CHECKBOX.exec(t);
    if (box) { listItem("task", `- [${box[1] === "☐" ? " " : "x"}] ${t.slice(box[0].length)}`); continue; }
    flushList();

    // A pasted HTML example is code: kept exactly, line for line, in a fence.
    const htmlEnd = readHtmlBlock(lines, i);
    if (htmlEnd !== null) {
      blocks.push(["```html", ...lines.slice(i, htmlEnd + 1), "```"].join("\n"));
      i = htmlEnd;
      continue;
    }

    // Tab-separated rows: two or more make a table, the first row its header.
    // A single row is a numbered section title ("01<TAB>Business Overview and
    // Goals") or a label with its text ("Ask this<TAB>Tell me about…").
    if (isTabRow(line)) {
      const { rows, used } = readTabRows(lines, i);
      i += used - 1;
      if (rows.length >= 2 && rows[0].length >= 2) { blocks.push(markdownTable(rows)); continue; }
      for (const [label, ...rest] of rows) {
        const value = rest.filter(Boolean).join(" · ");
        if (/^\d{1,3}$/.test(label) && rest.length === 1 && isTitleLike(value)) blocks.push(`## ${label} ${value}`);
        else if (label && value) blocks.push(`${bold(label)} ${value}`);
        else if (label || value) blocks.push(label || value);
      }
      continue;
    }

    const table = readTable(lines, i);
    // "Caption / Header / Header / …": the table may really start one line later.
    const shifted = table && readTable(lines, i + 1);
    if (table && shifted && tableScore(shifted.rows) > tableScore(table.rows)) {
      blocks.push(`### ${t}`);
      continue;
    }
    if (table) {
      blocks.push(markdownTable(table.rows));
      i += table.used - 1;
      continue;
    }

    if (isAllCaps(t) || isSectionTitle(t)) { blocks.push(`## ${t}`); continue; }

    // A heading is a short title line that STARTS a section: it follows a full
    // paragraph, a blank line, a list, a heading or the top of the document,
    // and a paragraph, list or table follows it — directly, or after one short
    // label line ("Objection: …" / "What this means" / <paragraph>), which is
    // kept as a bold label. Runs of short lines that are really cells of a
    // table we could not rebuild stay as plain lines rather than heading soup.
    if (isTitleLike(t)) {
      const prevIdx = (() => { let k = i - 1; while (k >= 0 && !lines[k].trim()) k--; return k; })();
      const prev = prevIdx >= 0 ? lines[prevIdx].trim() : undefined;
      const blankBefore = i > 0 && !lines[i - 1].trim();
      const startsSection =
        prev === undefined || blankBefore || prev.length > 80 || /[.!?:)"”']$/.test(prev) ||
        BULLET.test(prev) || CHECKBOX.test(prev) || isTabRow(prev) || isAllCaps(prev) || isSectionTitle(prev) || prev.includes("·");
      // A tab row opens a table ("Current Client Roster" / "Client<TAB>Category" …).
      const opensBody = (k: number) => {
        const line = lines[k];
        return line !== undefined && line.trim() !== "" &&
          (isLong(line) || BULLET.test(line.trim()) || CHECKBOX.test(line.trim()) || isTabRow(line) || readTable(lines, k) !== null);
      };
      const n1 = nextFilled(i + 1);
      if (startsSection && opensBody(n1)) { blocks.push(isCue(t) ? `**${t}**` : `### ${t}`); continue; }
      const n2 = nextFilled(n1 + 1);
      if (startsSection && lines[n1] !== undefined && isTitleLike(lines[n1]) && !isAllCaps(lines[n1]) && !isSectionTitle(lines[n1]) && opensBody(n2)) {
        blocks.push(isCue(t) ? `**${t}**` : `### ${t}`);
        blocks.push(`**${lines[n1].trim()}**`);
        i = n1;
        continue;
      }
    }

    blocks.push(t);
  }
  flushList();
  return blocks.map((b) => (b.startsWith("```") ? b : escapeTags(b))).join("\n\n");
}

/**
 * The Markdown serializer HTML-encodes `&`, `<` and `>` so its own parser can
 * round-trip them. The analyst reads `full_text` raw, so "Search & GEO" must not
 * be stored as "Search &amp; GEO". `&` is always safe to restore; `<` and `>`
 * only where they cannot start a tag ("CPA < $50", "ROAS > 4"), so nothing can
 * become live HTML on the next load.
 */
export function readableMarkdown(md: string): string {
  return oneBlankLine(unescapeWordChars(md
    .replace(/&lt;(?=[\s\d$=%.-]|$)/gm, "<")
    // ">" only after a space or digit: "ROAS > 4", "4>2". Never "<b>"-style.
    .replace(/(^|[\s\d])&gt;/gm, "$1>")
    .replace(/&amp;/g, "&")));
}

/**
 * The serializer escapes every "_" and "[", so a first save turned "utm_source" into
 * "utm\_source" and "[Client name]" into "\[Client name\]" in the text the AI analyst
 * reads. Undo the escapes the next load doesn't need: an underscore inside a word never
 * starts emphasis, and brackets not followed by "(", "[" or ":" are not a link.
 * Code (fenced blocks and `spans`) is left exactly as written.
 */
function unescapeWordChars(md: string): string {
  let fence = false;
  return md.split("\n").map((line) => {
    if (/^\s*(```|~~~)/.test(line)) { fence = !fence; return line; }
    if (fence) return line;
    return line.split(/(`[^`]*`)/).map((part, i) => (i % 2 ? part : part
      .replace(/(?<=[A-Za-z0-9])\\_(?=[A-Za-z0-9])/g, "_")
      .replace(/\\\[([^\]\n]*?)\\\](?![(\[:])/g, "[$1]"))).join("");
  }).join("\n");
}

/**
 * At most one blank line between blocks (code keeps its own). The serializer pads
 * tables with extra blank lines, which the next load turned into empty paragraphs,
 * so every save of a document with tables in a row added more space.
 */
function oneBlankLine(md: string): string {
  const out: string[] = [];
  let fence = false;
  for (const line of md.split("\n")) {
    if (!fence && !line.trim() && out.length && !out[out.length - 1].trim()) continue;
    out.push(line);
    if (/^\s*(```|~~~)/.test(line)) fence = !fence;
  }
  return out.join("\n");
}
